import { ASSOCIATED_ACCOUNT_FILTER_ID } from '@epicuro-next/constants/features/order-book';
import {
  ExportOrderBookExcelRequest,
  FiltersFieldIds,
  OrderFilterId,
} from '@epicuro-next/models/order-book';
import { utcDateTime } from '@epicuro-next/utilities';
import { FilterQueryItem } from 'graphql-api/baskets-api-interfaces';

type MappedFilters = Exclude<FiltersFieldIds, FiltersFieldIds.Address>;

const filtersV1ToV2Map: {
  [key in MappedFilters]: OrderFilterId;
} = {
  [FiltersFieldIds.OrderStatus]: OrderFilterId.IsDelivered,
  [FiltersFieldIds.OrderIncoming]: OrderFilterId.isOrderDelivered,
  [FiltersFieldIds.PlacedPeriod]: OrderFilterId.PlacedDate,
  [FiltersFieldIds.OrderType]: OrderFilterId.OrderType,
  [FiltersFieldIds.DeliveryYear]: OrderFilterId.Year,
  [FiltersFieldIds.DeliveryMonth]: OrderFilterId.Month,
  [FiltersFieldIds.Brand]: OrderFilterId.Brand,
  [FiltersFieldIds.SubBrand]: OrderFilterId.SubBrand,
  [FiltersFieldIds.Category]: OrderFilterId.Category,
  [FiltersFieldIds.Noos]: OrderFilterId.Noos,
  [FiltersFieldIds.AccountId]: OrderFilterId.Account,
  [FiltersFieldIds.Division]: OrderFilterId.Division,
  [FiltersFieldIds.MaterialGroup]: OrderFilterId.ProductType,
};

function getFilterV2Id(filterId: MappedFilters): OrderFilterId {
  return filtersV1ToV2Map[filterId];
}

export function mapFilterToExportOrderBookV2Request(
  filters: FilterQueryItem[],
  search: string,
): ExportOrderBookExcelRequest {
  const exportRequest: ExportOrderBookExcelRequest = {};

  const exportFilters: Record<string, string[]> = {};

  filters?.forEach((filter: FilterQueryItem) => {
    const fieldId = filter.fieldId as FiltersFieldIds;
    const values = filter.values as string[];

    if (fieldId === FiltersFieldIds.Address) {
      const address = JSON.parse(values[0]) as {
        addressId: string;
        accountId: string;
      };
      exportRequest.addresses = (exportRequest.addresses ?? []).concat({
        addressId: address.addressId,
        accountId: parseInt(address.accountId, 10),
      });

      return;
    }

    const filterV2Id = getFilterV2Id(fieldId);
    const valuesV2 = valueToValueV2(values, fieldId);

    if (fieldId === FiltersFieldIds.AccountId) {
      exportFilters[ASSOCIATED_ACCOUNT_FILTER_ID] = (
        exportFilters[ASSOCIATED_ACCOUNT_FILTER_ID] ?? []
      ).concat(values);
    }

    exportFilters[filterV2Id] = (exportFilters[filterV2Id] ?? []).concat(
      valuesV2,
    );
  });

  return {
    ...exportRequest,
    query: search ?? '',
    filters: exportFilters,
  };
}

function valueToValueV2(values: string[], fieldId: FiltersFieldIds): string[] {
  switch (fieldId) {
    case FiltersFieldIds.OrderStatus:
    case FiltersFieldIds.OrderIncoming:
      return values.map((v) => (v === 'false' ? 'true' : 'false'));
    case FiltersFieldIds.OrderType:
      return values.map((v) => (v === 'Now' ? '1' : '2'));
    case FiltersFieldIds.DeliveryYear:
      return [toDateString(values[0])];
    case FiltersFieldIds.DeliveryMonth:
      return [values[0]];
    case FiltersFieldIds.PlacedPeriod:
      return values.map(toDateString);
    default:
      return values;
  }
}

function toDateString(value: string) {
  return utcDateTime(value).toISODate();
}
